import Utils, { ServerModelWithDateStrings } from "@teamprevent/common/utils";

import axios from "axios";

import * as moment from "moment";

import { CustomerUserListModel, customerUserApi } from "../customerUser";

import { EmployeeRecordsDetailDto, parseEmployeeModel } from "../employeeRecord/model";

import { CustomerListModel, CustomerDetailModel, CustomerCreateModel, CustomerEditModel } from "./model";

class CustomerApi {

    private readonly apiUrl = "/api/customer";
    private abortController = new AbortController();

    public activate(id: any): Promise<void> {
        return axios.post(`${this.apiUrl}/Activate/` + id);
    }

    public deactivate(id: any): Promise<void> {
        return axios.post(`${this.apiUrl}/Deactivate/` + id);
    }

    public async getAll(): Promise<CustomerListModel[]> {
        const response = await axios.get(`${this.apiUrl}${Utils.getOnlyParameters()}`);
        const data = response.data as CustomerListModel[];
        return data.map(this.parseModel);
    }

    public async detail(id: any): Promise<CustomerDetailModel> {
        const query = "?parameters=" + Utils.getParameters();
        const response = await axios.get(`${this.apiUrl}/${id}${query}`);
        const model = response.data as CustomerDetailModel;
        return this.parseModel(model);
    }

    public async save(customer: CustomerEditModel): Promise<CustomerDetailModel> {
        const response = await axios.put(`${this.apiUrl}/${customer.id}`, customer);
        const model = response.data as CustomerDetailModel;
        return this.parseModel(model);
    }

    public async create(customer: CustomerCreateModel): Promise<CustomerDetailModel> {
        const response = await axios.post<CustomerDetailModel>(this.apiUrl, customer);
        const model = response.data;
        return this.parseModel(model);
    }

    public delete(id: any): Promise<void> {
        return axios.delete(`${this.apiUrl}/${id}`);
    }

    public async oldEmployeeRecords(filters?: {
        customerId: string;
        firstName?: string;
        lastName?: string;
        personalReferenceNumber?: string;
        dateOfBirth?: string;
    }):
        Promise<EmployeeRecordsDetailDto> {
        const { ...queryParams } = filters;
        const response = await axios.get<
            ServerModelWithDateStrings<EmployeeRecordsDetailDto>
        >(`${this.apiUrl}/${queryParams.customerId}/employeeRecords`, { params: queryParams });
        const data = response.data;
        const result: EmployeeRecordsDetailDto = {
            employeeRecords: data.employeeRecords.map(parseEmployeeModel),
            tooManyRecords: data.tooManyRecords
        };
        return result;
    }

    public async employeeRecords(filters?: {
        customerId: string;
        firstName?: string;
        lastName?: string;
        personalReferenceNumber?: string;
        dateOfBirth?: string;
        secondaryReference?: string;
    }):
        Promise<EmployeeRecordsDetailDto> {
        const { ...queryParams } = filters;
        const response = await axios.get<
            ServerModelWithDateStrings<EmployeeRecordsDetailDto>
        >(`${this.apiUrl}/${queryParams.customerId}/employeeRecords`, { params: queryParams });
        const data = response.data;
        const result: EmployeeRecordsDetailDto = {
            employeeRecords: data.employeeRecords.map(parseEmployeeModel),
            tooManyRecords: data.tooManyRecords
        };
        return result;
    }

    public async customerUsers(
        customerId: string,
        params: { firstName?: string; surname?: string; emailAddress?: string } = {}
    ): Promise<CustomerUserListModel[]> {
        this.abortController.abort();
        this.abortController = new AbortController();

        const response = await axios.get(`${this.apiUrl}/${customerId}/customerUsers`, { params, signal: this.abortController.signal });
        const data = response.data as CustomerUserListModel[];
        return data.map(customerUserApi.parseModel);
    }

    public async customerUsersByRole(customerId: string, params: { role: string }): Promise<CustomerUserListModel[]> {
        const response = await axios.get(`${this.apiUrl}/${customerId}/customerUsersByRole`, { params });
        const data = response.data as CustomerUserListModel[];
        return data.map(customerUserApi.parseModel);
    }

    public parseModel(model: CustomerDetailModel): CustomerDetailModel {
        const minDate = moment.utc("0001-01-01");

        const allowanceStartDate = moment(model.allowanceStartDate);
        const allowanceEndDate = moment(model.allowanceEndDate);

        return {
            ...model,
            allowanceStartDate: allowanceStartDate && allowanceStartDate.year() !== minDate.year() ? moment(model.allowanceStartDate) : undefined,
            allowanceEndDate: allowanceEndDate && allowanceStartDate.year() !== minDate.year() ? moment(model.allowanceEndDate)  : undefined,
        };
    }
}

export const customerApi = new CustomerApi();
